import React, { useState, useEffect } from 'react';
import {
  fetchPageMetrics,
  IPageMetrics,
  IPaginationInfo,
  IApiResponse
} from '../../api';
import { useWebsite } from '../../hooks/useWebsite';
import { useDateRange } from '../../hooks/useDateRange';
import Lookup from './Lookup';

type SortField = 'views' | 'visits' | 'sessions' | 'events';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

const PageTable = () => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const [data, setData] = useState<IPageMetrics[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<IPaginationInfo | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [lookup, setLookup] = useState<string | undefined>()
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'views',
    order: 'desc'
  });

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field && prevConfig.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const fetchData = async (page: number, append: boolean = false) => {
    setLoading(true);

    try {
      const response: IApiResponse<Array<IPageMetrics>> = await fetchPageMetrics(
        websiteId,
        {
          from,
          to,
          timezone,
          page,
          sort: sortConfig.field,
          order: sortConfig.order,
          limit: 50,
          lookup
        }
      );

      if (append) {
        setData(prevData => [...prevData, ...response.data]);
      } else {
        setData(response.data);
      }

      setPagination(response.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, false);
  }, [from, to, sortConfig, lookup, websiteId]);

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return null;
    }

    return sortConfig.order === 'desc'
      ? <i className="bi bi-chevron-down ms-1"></i>
      : <i className="bi bi-chevron-up ms-1"></i>;
  };

  const handleLoadMore = () => {
    if (pagination) {
      fetchData(currentPage + 1, true);
      setCurrentPage(currentPage + 1)
    }
  };

  const hasMorePages = pagination
    ? currentPage * pagination.items_per_page < pagination.total
    : false;

  return (
    <div className="card shadow-sm">
      <div className="card-header">
        <Lookup setTerms={setLookup} />
      </div>
      <div className="card-body py-4">
        <div className="table-responsive">
          <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
            <thead>
              <tr className="text-start text-muted fw-bolder fs-7 text-uppercase gs-0">
                <th scope="col">#</th>
                <th scope="col">Page</th>
                <th scope="col" role="button" className="text-center" onClick={() => handleSort('views')}>
                  Views {getSortIcon('views')}
                </th>
                <th scope="col" role="button" className="text-center" onClick={() => handleSort('visits')}>
                  Visits {getSortIcon('visits')}
                </th>
                <th scope="col" role="button" className="text-center" onClick={() => handleSort('sessions')}>
                  Sessions {getSortIcon('sessions')}
                </th>
                <th scope="col" role="button" className="text-center" onClick={() => handleSort('events')}>
                  Events {getSortIcon('events')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((item, index) => (
                <tr key={`page-${index}`} className="text-gray-600 fw-bold">
                  <td>#{index + 1}</td>
                  <td>
                    {item.page.length > 100 ? item.page.slice(0, 100) + "…" : item.page}
                  </td>
                  <td className="text-center">{item.views.toLocaleString()}</td>
                  <td className="text-center">{item.visits.toLocaleString()}</td>
                  <td className="text-center">{item.sessions.toLocaleString()}</td>
                  <td className="text-center">
                    {item.events.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.length === 0 && !loading && (
          <div className="text-center py-5 text-muted">
            <p>Aucune donnée disponible</p>
          </div>
        )}
      </div>

      {pagination && (
        <div className="card-footer bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Page {currentPage} sur {Math.ceil(pagination.total / pagination.items_per_page)} pages
            </small>

            {hasMorePages && (
              <button
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Chargement...
                  </>
                ) : (
                  <>
                    Charger plus
                    <i className="bi bi-arrow-down ms-2"></i>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageTable;
